import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Scrollbars } from 'react-custom-scrollbars';
import Raven from 'raven-js';

import Profiles from 'api/resources/profiles';
import getErrors from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import UserProfileForm from 'admin-moderator/containers/UserProfileForm';
import Loader from 'components/Loader';
import PhotoZoom from 'components/PhotoZoom';

import ErrorText from 'styled/Page/ErrorText';
import CenteredContainer from 'styled/Page/CenteredContainer';
import Label from 'styled/Label';
import Header from 'styled/Page/Header';
import HeaderTitle from 'styled/Page/HeaderTitle';

import Info from './Info';
import ImagesWrapper from './ImagesWrapper';

export class UserProfile extends React.PureComponent {
  state = {
    loading: false,
    errors: [],
    profile: undefined,
    profileData: undefined,
  }

  componentDidMount() {
    this.loadProfile();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.profileId !== this.props.match.params.profileId) {
      this.loadProfile();
    }
  }

  componentWillUnmount() {
    if (this.abortController) this.abortController.abort();
  }

  loadProfile = async () => {
    if (this.abortController) this.abortController.abort();
    if (typeof window.AbortController === 'function') {
      this.abortController = new window.AbortController();
    }

    this.setState({
      profile: undefined,
      loading: true,
      errors: [],
    });

    try {
      const response = await Profiles.get({
        profileId: this.props.match.params.profileId,
        signal: this.abortController ? this.abortController.signal : undefined,
      });

      const profile = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      let profileData;
      try {
        profileData = JSON.parse(String(profile.data));
      } catch (err) {
        this.setState({ errors: ['Невалидные данные'], loading: false });
        Raven.captureException(err, { extra: this.props });
        return;
      }

      this.setState({
        profile,
        profileData,
        loading: false,
      });
    } catch (err) {
      if (err.name === 'AbortError') return;

      this.setState({ errors: await getErrors(err), loading: false });

      Raven.captureException(err, { extra: this.props });
    }
  }

  render() {
    return (
      <Scrollbars autoHeight autoHeightMax="calc(100vh - 130px)">
        {this.state.profile && this.state.profileData && !this.state.loading && (
          <div style={{ paddingRight: '24px', paddingBottom: '40px' }}>
            {this.state.profileData.owner && (
              <React.Fragment>
                <Header style={{ margin: '0 0 24px', minHeight: 'auto' }}>
                  <HeaderTitle>Владелец паспорта</HeaderTitle>
                </Header>

                <Label>Фамилия</Label>

                <Info>{this.state.profileData.owner.last_name || 'Не указано'}</Info>

                <Label>Имя</Label>

                <Info>{this.state.profileData.owner.first_name || 'Не указано'}</Info>

                <Label>Отчество</Label>

                <Info>{this.state.profileData.owner.middle_name || 'Не указано'}</Info>

                <Label>Дата рождения</Label>

                <Info style={{ marginBottom: '31px' }}>
                  {this.state.profileData.owner.birthday || 'Не указано'}
                </Info>

                <Label>Место рождения</Label>

                <Info style={{ marginBottom: '31px' }}>
                  {this.state.profileData.owner.birth_place || 'Не указано'}
                </Info>

                <Label>Пол</Label>

                <Info style={{ marginBottom: '31px' }}>
                  {this.state.profileData.owner.gender === 'm' && 'МУЖСКОЙ'}
                  {this.state.profileData.owner.gender === 'f' && 'ЖЕНСКИЙ'}
                  {!this.state.profileData.owner.gender && 'Не указано'}
                </Info>
              </React.Fragment>
            )}

            {this.state.profileData.passport && (
              <React.Fragment>
                <Header style={{ margin: '24px 0', minHeight: 'auto' }}>
                  <HeaderTitle>Паспорт</HeaderTitle>
                </Header>

                <Label>Серия и номер</Label>

                <Info>
                  {this.state.profileData.passport.number || 'Не указано'}
                </Info>

                <Label>Кем выдан</Label>

                <Info style={{ textTransform: 'uppercase' }}>
                  {this.state.profileData.passport.subdivision_name || '-'}
                </Info>

                <Label>Дата выдачи</Label>

                <Info>
                  {this.state.profileData.passport.date_of_issue || '-'}
                </Info>

                <Label>Код подразделения</Label>

                <Info>
                  {this.state.profileData.passport.subdivision_code || '-'}
                </Info>
              </React.Fragment>
            )}

            {this.state.profileData.address && (
              <React.Fragment>
                <Header style={{ margin: '24px 0', minHeight: 'auto' }}>
                  <HeaderTitle>Адрес регистрации</HeaderTitle>
                </Header>

                <Label>Индекс</Label>

                <Info>
                  {this.state.profileData.address.zipcode || '-'}
                </Info>

                <Label>Город или населенный пункт</Label>

                <Info>
                  {this.state.profileData.address.city || '-'}
                </Info>

                <Label>Улица</Label>

                <Info>
                  {this.state.profileData.address.street || '-'}
                </Info>

                <Label>Дом</Label>

                <Info>
                  {this.state.profileData.address.house || '-'}
                </Info>

                <Label>Квартира</Label>

                <Info>
                  {this.state.profileData.address.flat || '-'}
                </Info>
              </React.Fragment>
            )}

            {(this.state.profileData.snils || this.state.profileData.inn) && (
              <React.Fragment>
                <Header style={{ margin: '24px 0', minHeight: 'auto' }}>
                  <HeaderTitle>Доп. документы</HeaderTitle>
                </Header>

                {this.state.profileData.snils && (
                  <React.Fragment>
                    <Label>Снилс</Label>

                    <Info>
                      {this.state.profileData.snils.number || '-'}
                    </Info>
                  </React.Fragment>
                )}

                {this.state.profileData.inn && (
                  <React.Fragment>
                    <Label>ИНН</Label>

                    <Info>
                      {this.state.profileData.inn.number || '-'}
                    </Info>
                  </React.Fragment>
                )}
              </React.Fragment>
            )}

            {((this.state.profileData.snils && this.state.profileData.snils.image)
              || (this.state.profileData.passport
                && this.state.profileData.passport.images
                && Object.keys(this.state.profileData.passport.images).length > 0)
              ) && (
              <React.Fragment>
                <Header style={{ margin: 0, minHeight: 'auto' }}>
                  <HeaderTitle>Прикрепленные фото</HeaderTitle>
                </Header>

                <ImagesWrapper>
                  {this.state.profileData.passport && this.state.profileData.passport.images && (
                    Object.keys(this.state.profileData.passport.images).map((key, index) => (
                      <PhotoZoom
                        src={this.state.profileData.passport.images[key].href}
                        alt={`Фото паспорта ${index + 1}`}
                        key={this.state.profileData.passport.images[key].href}
                      />
                    ))
                  )}

                  {this.state.profileData.snils && this.state.profileData.snils.image && (
                    <PhotoZoom
                      src={this.state.profileData.snils.image.href}
                      alt="Снилс"
                    />
                  )}

                  {(!this.state.profileData.snils || !this.state.profileData.snils.image)
                    &&
                    (!this.state.profileData.passport
                      || !this.state.profileData.passport.images
                      || Object.keys(this.state.profileData.passport.images).length === 0)
                    && (
                    'Нет прикрепленных фото'
                  )}
                </ImagesWrapper>
              </React.Fragment>
            )}

            {this.state.profile && this.state.profile.status !== 'accepted' && (
              <UserProfileForm
                id={this.props.match.params.profileId}
                comment={this.state.profile && this.state.profile.comment}
                onStatusUpdate={this.props.onStatusUpdate}
                status={this.state.profile && this.state.profile.status}
                key={this.state.profile && this.state.profile.comment}
              />
            )}
          </div>
        )}

        {this.state.loading && (
          <CenteredContainer><Loader /></CenteredContainer>
        )}

        {!this.state.loading && this.state.errors.length > 0 && (
          <CenteredContainer>
            {this.state.errors.map(error => (
              <ErrorText role="alert" key={error}>{error}</ErrorText>
            ))}
          </CenteredContainer>
        )}
      </Scrollbars>
    );
  }
}

UserProfile.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      profileId: PropTypes.string.isRequired,
      status: PropTypes.oneOf(['created', 'rejected', 'accepted']).isRequired,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }),
  onStatusUpdate: PropTypes.func,
};

UserProfile.defaultProps = {
  history: {
    push: () => {},
    location: {},
  },
  onStatusUpdate: () => {},
};

export default withRouter(UserProfile);
